import React, { useRef } from 'react';
import { Pencil, Repeat } from 'lucide-react';
import useTrainingPlanStore from '../../stores/trainingPlanStore';
import { useTheme } from '../../context/ThemeContext';
import DayCell from './DayCell';

const DAY_SHORT_EN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_SHORT_HE = ['א׳', 'ב׳', 'ג׳', 'ד׳', 'ה׳', 'ו׳', 'ש׳'];

export default function WeekRow({ weekId, emptyColumns }) {
 const { themeClasses } = useTheme();
 const week = useTrainingPlanStore(state => state.weeks[weekId]);
 const updateWeek = useTrainingPlanStore(state => state.updateWeek);
 const isRepeating = useTrainingPlanStore(state => state.meta.is_repeating);
 const durationWeeks = useTrainingPlanStore(state => state.meta.duration_weeks);
 const weekOrder = useTrainingPlanStore(state => state.weekOrder);

 const language = localStorage.getItem('language') || 'english';
 const he = language === 'hebrew';
 const inputRef = useRef(null);

 if (!week) return null;

 const runWeeks = durationWeeks || 1;
 const isFirstWeek = weekOrder[0] === weekId;
 const showRepeatBadge = isRepeating && isFirstWeek;

 return (
 <div className="flex flex-col lg:flex-row group/week items-stretch gap-2 mb-6 lg:mb-4">
 <div className={`lg:w-40 shrink-0 px-4 py-3 lg:p-4 flex flex-row lg:flex-col items-center lg:items-stretch lg:justify-center gap-3 lg:gap-0 rounded-lg border border-line dark:border-line bg-surface dark:bg-surface/[0.06] relative`}>
 <div className="flex flex-col gap-1.5 lg:mb-2 shrink-0">
 <h3 className={`font-semibold text-lg lg:text-2xl tracking-tight ${themeClasses.textPrimary}`}>
 {he ? 'שבוע' : 'Week'} {week.week_number}
 </h3>
 {showRepeatBadge && (
 <span className="inline-flex items-center gap-1 w-fit rounded-full border border-forest-ondark/30 bg-forest/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-forest-hover dark:text-forest-ondark">
 <Repeat size={10} strokeWidth={2.5} />
 {runWeeks > 1
 ? (he ? `חוזר ל-${runWeeks} שבועות` : `Repeats for ${runWeeks} weeks`)
 : (he ? 'חוזר' : 'Repeats')}
 </span>
 )}
 </div>
 <div
 className="relative flex items-center group/input cursor-text w-full"
 onClick={() => inputRef.current?.focus()}
 >
 <input
 ref={inputRef}
 type="text"
 value={week.focus}
 onChange={(e) => updateWeek(weekId, { focus: e.target.value })}
 placeholder={he ? 'שם שבוע...' : 'Week name...'}
 className="w-full min-h-9 lg:min-h-0 bg-transparent border-b border-line lg:border-transparent group-hover/input:border-line dark:border-line-dark lg:dark:border-transparent dark:group-hover/input:border-line-dark focus:border-forest outline-none text-sm text-ink-muted dark:text-ink-dark placeholder:text-ink-muted font-medium transition-colors pe-6 truncate pb-0.5"
 />
 <Pencil size={12} className="absolute end-1 text-ink-muted opacity-60 lg:opacity-0 group-hover/input:opacity-100 transition-opacity" />
 </div>
 </div>

 <div className="flex-1 flex flex-col lg:flex-row gap-2">
 {week.slots.map((slot, index) => {
 const isHidden = emptyColumns.has(index);
 return (
 <div
 key={slot.slot_id}
 className={`transition-[opacity,transform] duration-300 ease-out ${isHidden ? 'hidden lg:block lg:w-0 opacity-0 overflow-hidden' : 'flex items-stretch gap-2 lg:block lg:flex-1 lg:min-w-[120px] opacity-100'}`}
 >
 {/* Below lg the days stack, so each one carries its own name. */}
 <span className="lg:hidden w-12 shrink-0 pt-3 text-[11px] font-bold uppercase tracking-wider text-ink-muted">
 {(he ? DAY_SHORT_HE : DAY_SHORT_EN)[index]}
 </span>
 <div className="min-w-0 flex-1">
 <DayCell weekId={weekId} slotIndex={index} isHidden={isHidden} />
 </div>
 </div>
 );
 })}
 </div>
 </div>
 );
}
